"use client";

import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { WalletPortfolioContext } from "../providers/wallet-portfolio-provider";
import {
  SectionStatusContext,
  type SectionStatus,
} from "../providers/section-status-provider";
import type { Environment, Theme } from "../../../types";
import type { OnOutlink } from "../../../lib/outlink";
import { cn } from "../../../lib/utils";
import { resolveEnvironment } from "../../../lib/environment";
import {
  BackArrowIcon,
  RefreshIcon,
} from "./icons";
import { CopyIcon, CheckIcon } from "../../wallet-info/components/icons";
import { getPortraitUrl } from "../../wallet-info/constants";
import { formatWalletAddress } from "../utils";
import { TrackingBoundary } from "../../../lib/sentry/index";
import { RewardsSection } from "./sections/RewardsSection";
import { PointsSection } from "./sections/PointsSection";
import { StakeSection } from "./sections/StakeSection";
import { GameSwapSection } from "./sections/GameSwapSection";
import { ForgeSection } from "./sections/ForgeSection";
import { CrossdPoolSection } from "./sections/CrossdPoolSection";
import {
  PORTFOLIO_SECTIONS,
  type EstimateGasFn,
  type LpBalanceReaderFn,
  type PortfolioSection,
  type SendTransactionFn,
  type StakingRewardsReaderFn,
} from "../types";
import "../styles/portfolio.css";

const POLL_SEC = 15;
const WP_QUERY_PREFIX = "wp:";

export interface WalletPortfolioBodyProps {
  env?: Environment;
  theme?: Theme;
  walletAddress: string;
  /**
   * 헤더 좌측 아바타 옆에 표시할 지갑 이름 (예: "Account 1").
   * 전달되지 않으면 텍스트는 숨겨지고 아바타만 표시된다.
   */
  walletName?: string;
  /** 왼쪽 상단 back 버튼 클릭 핸들러. 없으면 back 버튼 숨김. */
  onBack?: () => void;
  /** 헤더 노출 여부. embed 시 외부 헤더를 쓰면 false로 숨길 수 있음. */
  showHeader?: boolean;
  /**
   * 컨테이너 variant.
   * - `"embed"`(기본): 부모 영역을 채우도록 `.wp-embed` 래퍼로 감쌈
   * - `"fullscreen"`: 고정 풀스크린 `.wp-fullscreen` 래퍼로 감쌈
   * - `"none"`: 래퍼 div 없이 본문만 반환 (Drawer.Content 등 외부 래퍼에 클래스를 직접 줄 때)
   */
  variant?: "fullscreen" | "embed" | "none";
  className?: string;
  /**
   * 포트폴리오 섹션 내부 외부 링크 이동을 가로채는 콜백.
   * 반환값으로 URL 변형 / 취소가 가능하며 async도 지원한다.
   */
  onOutlink?: OnOutlink;
  /**
   * game-swap LP 섹션이 사용자별 LP 잔고/지분율을 온체인에서 보강하기 위해
   * host(wagmi 보유 측)로부터 주입받는 reader. 미주입 시 풀 정보만 표시한다.
   */
  lpBalanceReader?: LpBalanceReaderFn;
  /**
   * CROSS Staking 섹션이 온체인 `earned()` / `balanceOf()`를 보강하기 위해
   * host(wagmi 보유 측)로부터 주입받는 reader. 미주입 시 API 값으로 폴백한다.
   */
  stakingRewardsReader?: StakingRewardsReaderFn;
  /**
   * 내장 Withdraw(Rewards) 트랜잭션 서명/브로드캐스트 콜백.
   * wagmi의 `sendTransactionAsync`를 그대로 전달해도 호환된다.
   * 미주입 시 Withdraw는 기존 외부 링크로 폴백한다.
   */
  sendTransaction?: SendTransactionFn;
  /** 내장 Withdraw 확인 화면의 가스 추정 콜백. 미주입 시 지갑 추정에 위임. */
  estimateGas?: EstimateGasFn;
  /**
   * 포트폴리오 금액 표시 통화의 USD 환산 비율(USD 기준). 미주입 시 1(=USD).
   */
  conversionRatio?: number;
  /** 포트폴리오 금액 표시 통화 기호. 미주입 시 `"$"`. */
  currencySymbol?: string;
  /**
   * 노출할 포트폴리오 섹션을 제한한다. 미지정(`undefined`)이면 모든 섹션을
   * 기본 순서대로 표시하고, 배열을 주면 포함된 섹션만 (기본 순서를 유지한 채)
   * 표시한다. 빈 배열이면 아무 섹션도 표시하지 않는다.
   */
  sections?: PortfolioSection[];
}

export function WalletPortfolioBody({
  env,
  theme = "light",
  walletAddress,
  walletName,
  onBack,
  showHeader = true,
  variant = "embed",
  className,
  onOutlink,
  lpBalanceReader,
  stakingRewardsReader,
  sendTransaction,
  estimateGas,
  conversionRatio,
  currencySymbol,
  sections,
}: WalletPortfolioBodyProps) {
  const resolvedEnv = resolveEnvironment(env);
  // 미지정이면 전체 노출, 배열이면 포함된 섹션만 노출(빈 배열 = 전부 숨김).
  const isVisible = React.useCallback(
    (section: PortfolioSection) => !sections || sections.includes(section),
    [sections],
  );
  const hasAddress = !!walletAddress;
  const queryClient = useQueryClient();

  // 각 섹션이 보고한 로딩/보유 상태. 노출 대상 섹션이 전부 "자산 없음"으로
  // 확정되면 전역 빈 화면을, 아직 아무것도 확정되지 않았으면 로더를 그린다.
  const [sectionStatus, setSectionStatus] = React.useState<
    Partial<Record<PortfolioSection, SectionStatus>>
  >({});

  const reportSectionStatus = React.useCallback(
    (key: PortfolioSection, status: SectionStatus) => {
      setSectionStatus((prev) => {
        const cur = prev[key];
        if (cur && cur.loading === status.loading && cur.empty === status.empty) {
          return prev;
        }
        return { ...prev, [key]: status };
      });
    },
    [],
  );

  const unregisterSection = React.useCallback((key: PortfolioSection) => {
    setSectionStatus((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const sectionStatusContext = React.useMemo(
    () => ({ report: reportSectionStatus, unregister: unregisterSection }),
    [reportSectionStatus, unregisterSection],
  );

  const [countdown, setCountdown] = React.useState(POLL_SEC);
  const [spinning, setSpinning] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  const invalidateAll = React.useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey[0] === "string" &&
        query.queryKey[0].startsWith(WP_QUERY_PREFIX),
    });
  }, [queryClient]);

  const triggerRefresh = React.useCallback(() => {
    invalidateAll();
    setSpinning(true);
    setTimeout(() => setSpinning(false), 600);
    setCountdown(POLL_SEC);
  }, [invalidateAll]);

  React.useEffect(() => {
    if (!hasAddress) return;
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          triggerRefresh();
          return POLL_SEC;
        }
        return prev - 1;
      });
    }, 1_000);
    return () => clearInterval(id);
  }, [hasAddress, triggerRefresh]);

  const close = React.useCallback(() => {
    onBack?.();
  }, [onBack]);

  const portraitUrl = hasAddress
    ? getPortraitUrl(resolvedEnv, walletAddress, { size: 64 })
    : undefined;

  const handleCopyAddress = React.useCallback(async () => {
    if (copied || !walletAddress) return;
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard 미지원/거부 시 무시 */
    }
  }, [walletAddress, copied]);

  React.useEffect(() => () => clearTimeout(copyTimerRef.current), []);

  const contextValue = React.useMemo(
    () => ({
      env: resolvedEnv,
      theme,
      open: true,
      walletAddress,
      close,
      onOutlink,
      lpBalanceReader,
      stakingRewardsReader,
      sendTransaction,
      estimateGas,
      conversionRatio,
      currencySymbol,
    }),
    [
      resolvedEnv,
      theme,
      walletAddress,
      close,
      onOutlink,
      lpBalanceReader,
      stakingRewardsReader,
      sendTransaction,
      estimateGas,
      conversionRatio,
      currencySymbol,
    ],
  );

  // 노출 대상 섹션 전체가 로딩을 끝내고 "자산 없음"으로 확정된 경우에만
  // 전역 빈 화면을 그린다. 하나라도 아직 로딩 중이면(내용도 없으면) 로더 표시.
  const expectedSections = PORTFOLIO_SECTIONS.filter(isVisible);
  const allSettled =
    expectedSections.length > 0 &&
    expectedSections.every((key) => {
      const status = sectionStatus[key];
      return !!status && !status.loading;
    });
  const allEmpty =
    allSettled && expectedSections.every((key) => sectionStatus[key]?.empty);
  const anyContent = expectedSections.some((key) => {
    const status = sectionStatus[key];
    return !!status && !status.empty;
  });
  const showLoader =
    expectedSections.length > 0 && !allSettled && !anyContent;

  const inner = (
    <>
      {showHeader && (
        <div className="wp-header">
          <div className="wp-header-account">
            {onBack && (
              <button
                type="button"
                className="wp-header-btn"
                aria-label="Back"
                data-track="back"
                onClick={onBack}
              >
                <BackArrowIcon />
              </button>
            )}
            <span className="wp-header-avatar" aria-hidden>
              {portraitUrl && (
                <img
                  src={portraitUrl}
                  alt=""
                  className="wp-header-avatar-img"
                  draggable={false}
                />
              )}
            </span>
            {(walletName || walletAddress) && (
              <div className="wp-header-account-meta">
                {walletName && (
                  <span className="wp-header-wallet-name" title={walletName}>
                    {walletName}
                  </span>
                )}
                {walletAddress && (
                  <button
                    type="button"
                    className="wp-header-address-btn"
                    onClick={handleCopyAddress}
                    aria-label="Copy wallet address"
                    data-track="address_copy"
                  >
                    <span className="wp-header-address">
                      {formatWalletAddress(walletAddress)}
                    </span>
                    <span
                      className="wp-header-address-copy"
                      data-copied={copied}
                    >
                      {copied ? (
                        <CheckIcon size={14} />
                      ) : (
                        <CopyIcon size={14} />
                      )}
                    </span>
                  </button>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            className="wp-header-btn wp-refresh-btn"
            aria-label="Refresh portfolio"
            data-track="refresh"
            onClick={triggerRefresh}
          >
            <RefreshIcon className={spinning ? "wp-spin-once" : undefined} />
            {hasAddress && (
              <span className="wp-refresh-badge">{countdown}</span>
            )}
          </button>
        </div>
      )}

      <div className="wp-body">
        <div className="wp-body-inner" data-empty={allEmpty || undefined}>
          {isVisible("rewards") && <RewardsSection />}
          {isVisible("points") && <PointsSection />}
          {isVisible("staking") && <StakeSection />}
          {isVisible("gameSwap") && <GameSwapSection />}
          {isVisible("forge") && <ForgeSection />}
          {isVisible("crossdPool") && <CrossdPoolSection />}
          {showLoader && (
            <div className="wp-portfolio-loading" role="status" aria-label="Loading portfolio">
              <span className="wp-portfolio-spinner" aria-hidden />
            </div>
          )}
          {allEmpty && (
            <div className="wp-portfolio-empty" role="status">
              <span className="wp-portfolio-empty-icon" aria-hidden>
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 7H5a2 2 0 0 1-2-2 2 2 0 0 1 2-2h13v4" />
                  <path d="M3 5v13a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1" />
                  <circle cx="16.5" cy="13.5" r="1" fill="currentColor" stroke="none" />
                </svg>
              </span>
              <p className="wp-portfolio-empty-title">No assets yet</p>
              <p className="wp-portfolio-empty-desc">
                Assets you hold across ONE services will appear here.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <WalletPortfolioContext.Provider value={contextValue}>
      <SectionStatusContext.Provider value={sectionStatusContext}>
        <TrackingBoundary feature="wallet_portfolio">
          {variant === "none" ? (
            inner
          ) : (
            <div
              className={cn(
                variant === "fullscreen" ? "wp-fullscreen" : "wp-embed",
                className,
              )}
              data-theme={theme}
            >
              {inner}
            </div>
          )}
        </TrackingBoundary>
      </SectionStatusContext.Provider>
    </WalletPortfolioContext.Provider>
  );
}
